import { useTranslation } from "react-i18next";
import { refreshConnectorCapabilities, type ListedConnector } from "../../../api/connectorsApi";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import CommandFold from "../CommandFold";
import { PcPresence } from "../management/ConnectorPcLine";
import ConnectorCommandLine from "../ConnectorCommandLine";
import ProviderCards from "../providers/ProviderCards";
import ConnectSteps from "./ConnectSteps";
import { pcConnectorsOf, reportedPcsOf } from "./setupProgress";
import ButtonLink from "../../../ui/buttons/ButtonLink";

/**
 * The guide's step bodies (onboarding.md OB-1), each standing alone. The connector comes as an
 * installer from this server's release channel (F47, OB-Q1); the pair command stays the
 * alternative for people who use a terminal.
 */

export function WelcomeStep({ busy, onStart, onNotNow }: { busy: boolean; onStart: () => void; onNotNow: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <p className="app-text text-sm">{t("connectors.setup.welcome.body")}</p>
      <div className="flex flex-wrap gap-2">
        <AppButton variant="primary" size="sm" disabled={busy} onClick={onStart}>{t("connectors.setup.welcome.start")}</AppButton>
        <AppButton variant="secondary" size="sm" disabled={busy} onClick={onNotNow}>{t("connectors.setup.welcome.notNow")}</AppButton>
      </div>
    </div>
  );
}

export function ServerAddressStep({ isAdministrator }: { isAdministrator: boolean }) {
  const { t } = useTranslation();
  return isAdministrator ? (
    <p className="app-text text-sm">{t("connectors.setup.serverAddress.administrator")}</p>
  ) : (
    <StatusMessage tone="info">{t("connectors.setup.serverAddress.member")}</StatusMessage>
  );
}

/** Steps 2 and 3 together: the server cannot see an installation, only a pairing (F78: get it, open it, enter its code). */
export function PairStep({ waiting }: { waiting: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <ConnectSteps />
      {waiting ? <p className="app-text-muted text-sm" role="status">{t("connectors.setup.pair.waiting")}</p> : null}
    </div>
  );
}

/**
 * Paired, not connected yet (F118). The tray starts the connector after pairing. Each PC's line is
 * what the server knows of it (`PcPresence`, as on the Connectors page): why it hasn't connected is
 * the tray's to say, in its menu, where it is also started (P-B); the web detects nothing of its
 * own. The command is only the folded backup (F47, P-F).
 */
export function StartStep({ connectors }: { connectors: readonly ListedConnector[] }) {
  const { t, i18n } = useTranslation();
  const pcConnectors = pcConnectorsOf(connectors);
  const pcs = new Intl.ListFormat(i18n.resolvedLanguage, { type: "conjunction" }).format(pcConnectors.map((connector) => connector.name));
  return (
    <div className="space-y-2">
      <p className="app-text text-sm">{t("connectors.setup.start.body", { pcs })}</p>
      <ul className="space-y-1 text-xs">
        {pcConnectors.map((connector) => (
          <li key={connector.connectorId} className="flex flex-wrap gap-x-2">
            <span className="app-title font-semibold">{connector.name}</span>
            <PcPresence connector={connector} />
          </li>
        ))}
      </ul>
      <p className="app-text-muted text-sm" role="status">{t("connectors.setup.pair.waiting")}</p>
      <CommandFold><ConnectorCommandLine command={{ args: "run", aboutServer: true }} /></CommandFold>
    </div>
  );
}

/** Step 5, with the optional API keys (step 6): one usable route is enough; the cards connect them (F49). */
export function AgentsStep({ connectors, onChecked }: { connectors: readonly ListedConnector[]; onChecked: () => void }) {
  const { t } = useTranslation();
  const pcs = reportedPcsOf(connectors);
  const online = pcs.filter((connector) => connector.status.online);
  const checkAgain = () => {
    // A free re-read on each connected PC (D17); its next report arrives with the next poll.
    void Promise.allSettled(online.map((connector) => refreshConnectorCapabilities(connector.connectorId, null))).then(onChecked);
  };
  return (
    <div className="space-y-3">
      <p className="app-text text-sm">{t("connectors.setup.agents.needOne")}</p>
      <ProviderCards connectors={pcs} />
      <AppButton variant="secondary" size="xs" disabled={online.length === 0} onClick={checkAgain}>{t("connectors.setup.check")}</AppButton>
    </div>
  );
}

export function DoneStep() {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <StatusMessage tone="success" title={t("connectors.setup.done.title")}>
        <p>{t("connectors.setup.done.body")}</p>
      </StatusMessage>
      <div className="flex flex-wrap gap-2">
        <ButtonLink to={appPaths.sources()} variant="primary">{t("connectors.setup.done.startThread")}</ButtonLink>
        <ButtonLink to={appPaths.profileConnectors()}>{t("connectors.setup.done.connectors")}</ButtonLink>
      </div>
    </div>
  );
}
